import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X, Sparkles, Moon, Sun, Wind, Heart, Brain, Leaf } from "lucide-react";
import { GlassCard } from "./GlassCard";

// Curated meditation videos from YouTube (relaxation, meditation, mindfulness)
const MEDITATION_CATEGORIES = [
  {
    id: "calm",
    name: "Calma",
    icon: Moon,
    color: "text-indigo-400",
    bgColor: "bg-indigo-500/10",
    borderColor: "border-indigo-500/30",
    videos: [
      { id: "lFcSrYw-ARY", title: "Meditación para Calmar la Mente", duration: "10 min" },
      { id: "inpok4MKVLM", title: "Sonidos de Lluvia Relajante", duration: "3 hrs" },
      { id: "1ZYbU82GVz4", title: "Música para Dormir Profundo", duration: "8 hrs" },
    ]
  },
  {
    id: "energy",
    name: "Energía",
    icon: Sun,
    color: "text-amber-400",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/30",
    videos: [
      { id: "g_tea8ZNk5A", title: "Meditación Matutina Energizante", duration: "10 min" },
      { id: "OJFX8MBgbyA", title: "Música para Concentración", duration: "2 hrs" },
      { id: "WPni755-Krg", title: "Yoga para Despertar", duration: "15 min" },
    ]
  },
  {
    id: "anxiety",
    name: "Ansiedad",
    icon: Wind,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    borderColor: "border-cyan-500/30",
    videos: [
      { id: "O-6f5wQXSu8", title: "Respiración 4-7-8 para Ansiedad", duration: "5 min" },
      { id: "MIr3RsUWrdo", title: "Meditación Guiada Anti-Ansiedad", duration: "15 min" },
      { id: "aXItOY0sLRY", title: "Sonidos de Naturaleza Calmante", duration: "1 hr" },
    ]
  },
  {
    id: "self-love",
    name: "Amor Propio",
    icon: Heart,
    color: "text-pink-400",
    bgColor: "bg-pink-500/10",
    borderColor: "border-pink-500/30",
    videos: [
      { id: "itZMM5gCboo", title: "Meditación de Amor Propio", duration: "12 min" },
      { id: "6p_yaNFSYao", title: "Afirmaciones Positivas", duration: "10 min" },
      { id: "szj59j0h-Dk", title: "Gratitud y Autocompasión", duration: "15 min" },
    ]
  },
  {
    id: "focus",
    name: "Concentración",
    icon: Brain,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    videos: [
      { id: "hHWgGfZpk00", title: "Música Lo-Fi para Estudiar", duration: "1 hr" },
      { id: "lTRiuFIWV54", title: "Ondas Alfa para Concentración", duration: "2 hrs" },
      { id: "5qap5aO4i9A", title: "Lo-Fi Hip Hop Radio", duration: "Live" },
    ]
  },
  {
    id: "nature",
    name: "Naturaleza",
    icon: Leaf,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
    videos: [
      { id: "eKFTSSKCzWA", title: "Bosque con Pájaros Cantando", duration: "3 hrs" },
      { id: "V1RPi2MYptM", title: "Olas del Mar Relajantes", duration: "8 hrs" },
      { id: "q76bMs-NwRk", title: "Cascada en el Bosque", duration: "2 hrs" },
    ]
  },
];

export const MeditationPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);

  const handlePlayVideo = (video, category) => {
    setPlayingVideo({ ...video, category });
  };

  const handleCloseVideo = () => {
    setPlayingVideo(null);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-orbitron text-white flex items-center justify-center gap-3">
          <Sparkles className="w-8 h-8 text-neon-purple" />
          Meditaciones
        </h1>
        <p className="text-slate-400 mt-2 font-inter">
          Encuentra paz interior con nuestras meditaciones guiadas
        </p>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {MEDITATION_CATEGORIES.map((category) => {
          const Icon = category.icon;
          const isSelected = selectedCategory?.id === category.id;
          
          return (
            <motion.button
              key={category.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedCategory(isSelected ? null : category)}
              className={`p-4 rounded-xl border transition-all ${
                isSelected
                  ? `${category.bgColor} ${category.borderColor} ring-2 ring-offset-2 ring-offset-void ring-${category.color.split('-')[1]}-500/50`
                  : "bg-white/5 border-white/10 hover:bg-white/10"
              }`}
              data-testid={`meditation-category-${category.id}`}
            >
              <Icon className={`w-8 h-8 mx-auto mb-2 ${category.color}`} />
              <span className={`text-sm font-rajdhani ${isSelected ? category.color : 'text-slate-300'}`}>
                {category.name}
              </span>
            </motion.button>
          );
        })}
      </div>

      {/* Videos List */}
      <AnimatePresence mode="wait">
        {selectedCategory && (
          <motion.div
            key={selectedCategory.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <GlassCard className="p-6">
              <h2 className={`font-orbitron text-lg mb-4 flex items-center gap-2 ${selectedCategory.color}`}>
                {(() => {
                  const Icon = selectedCategory.icon;
                  return <Icon className="w-5 h-5" />;
                })()}
                {selectedCategory.name}
              </h2>
              
              <div className="grid gap-4">
                {selectedCategory.videos.map((video) => (
                  <motion.div
                    key={video.id}
                    whileHover={{ scale: 1.01 }}
                    className={`flex items-center gap-4 p-4 rounded-lg ${selectedCategory.bgColor} border ${selectedCategory.borderColor} cursor-pointer group`}
                    onClick={() => handlePlayVideo(video, selectedCategory)}
                    data-testid={`meditation-video-${video.id}`}
                  >
                    {/* Thumbnail */}
                    <div className="relative w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-black/50">
                      <img
                        src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                        alt={video.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                        <Play className={`w-8 h-8 ${selectedCategory.color} fill-current`} />
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-rajdhani text-sm truncate">
                        {video.title}
                      </h3>
                      <span className="text-xs text-slate-400">
                        {video.duration}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Player Modal */}
      <AnimatePresence>
        {playingVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={handleCloseVideo}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-4xl bg-void-paper rounded-2xl border border-white/10 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-4 border-b border-white/10 ${playingVideo.category.bgColor}`}>
                <div className="flex items-center gap-3">
                  {(() => {
                    const Icon = playingVideo.category.icon;
                    return <Icon className={`w-5 h-5 ${playingVideo.category.color}`} />;
                  })()}
                  <div>
                    <h3 className="text-white font-rajdhani">{playingVideo.title}</h3>
                    <span className="text-xs text-slate-400">{playingVideo.duration}</span>
                  </div>
                </div>
                <button
                  onClick={handleCloseVideo}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="close-meditation-video"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              
              {/* Video Player */}
              <div className="relative pt-[56.25%] bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${playingVideo.id}?autoplay=1&rel=0`}
                  title={playingVideo.title}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tip Card */}
      {!selectedCategory && (
        <GlassCard className="p-6 text-center">
          <Sparkles className="w-12 h-12 text-neon-purple mx-auto mb-4 opacity-50" />
          <p className="text-slate-400 font-inter">
            Selecciona una categoría para ver las meditaciones disponibles
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Dedica unos minutos al día para cuidar tu bienestar mental
          </p>
        </GlassCard>
      )}
    </motion.div>
  );
};
